import { Badge, Box, Tab, Tabs } from '@mui/material'
import { useEffect } from 'react'
import type { ChangeEvent, JSX } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Outlet,
  useNavigate,
  useLocation,
  useRevalidator,
} from 'react-router-dom'

import {
  useDurationTestCount,
  useFinishDurationTestCount,
} from '@/hooks/useLoader'
import { layoutStyles, theme } from '@/utils/theme'

const DurationTestPage = (): JSX.Element => {
  const { t } = useTranslation('app')
  const navigate = useNavigate()
  const location = useLocation()
  const revalidator = useRevalidator()
  const { durationTestCount } = useDurationTestCount()
  const { finishCount } = useFinishDurationTestCount()

  // Get current tab from location path
  const currentTab = location.pathname.split('/').pop() ?? 'start-test'

  // Auto-refresh counts every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      revalidator.revalidate()
    }, 10000)

    // Cleanup interval on component unmount
    return () => clearInterval(interval)
  }, [revalidator])

  const handleTabChange = (event: ChangeEvent<unknown>, newValue: number) => {
    const tabRoutes = ['start', 'in-progress', 'finish']

    // Preserve existing query parameters
    navigate({
      pathname: `/v2/main/device/duration-test/${tabRoutes[newValue]}`,
      search: location.search,
    })
  }

  const getTabValue = () => {
    const tabRoutes = ['start', 'in-progress', 'finish']
    const index = tabRoutes.indexOf(currentTab)
    return index === -1 ? 0 : index
  }

  return (
    <Box
      sx={{
        ...layoutStyles.pageContainer,
        mt: 0,
        p: 2,
      }}
      id={'page-container-box'}
    >
      <Box sx={{ ...layoutStyles.tabsContainer }} id={'tab-container-box'}>
        <Tabs
          value={getTabValue()}
          onChange={handleTabChange}
          aria-label="duration-test-tabs"
          variant="standard"
          sx={{
            '& .MuiTabs-indicator': {
              backgroundColor: theme.palette.secondary.main,
              height: 3,
            },
            '& .MuiTabs-flexContainer': {
              justifyContent: 'flex-start',
            },
            mb: 2,
          }}
        >
          <Tab
            label={t('device.durationTest.startDurationTest')}
            id={`tab-0`}
            aria-controls={`tabpanel-0`}
            sx={{
              fontSize: '0.875rem',
              fontWeight: 400,
              width: '160px',
              maxWidth: '264px',
              minWidth: '72px',
              textTransform: 'uppercase',
              '&.Mui-selected': {
                color: theme.palette.text.primary,
              },
            }}
          />
          <Tab
            label={
              durationTestCount > 0 ? (
                <Badge badgeContent={durationTestCount} color="primary">
                  {t('device.durationTest.inDurationTest')}
                </Badge>
              ) : (
                t('device.durationTest.inDurationTest')
              )
            }
            id={`tab-1`}
            aria-controls={`tabpanel-1`}
            sx={{
              fontSize: '0.875rem',
              fontWeight: 400,
              width: '160px',
              maxWidth: '264px',
              minWidth: '72px',
              textTransform: 'uppercase',
              '&.Mui-selected': {
                color: theme.palette.text.primary,
              },
            }}
          />
          <Tab
            label={
              finishCount > 0 ? (
                <Badge badgeContent={finishCount} color="error">
                  {t('device.durationTest.finishDurationTest')}
                </Badge>
              ) : (
                t('device.durationTest.finishDurationTest')
              )
            }
            id={`tab-2`}
            aria-controls={`tabpanel-2`}
            sx={{
              fontSize: '0.875rem',
              fontWeight: 400,
              width: '160px',
              maxWidth: '264px',
              minWidth: '72px',
              textTransform: 'uppercase',
              '&.Mui-selected': {
                color: theme.palette.text.primary,
              },
            }}
          />
        </Tabs>
        <Outlet />
      </Box>
    </Box>
  )
}

export default DurationTestPage
